/**
 * Two small per-browser facts, kept in localStorage.
 *
 * Every access is wrapped: Safari in private mode throws on localStorage, and neither of these
 * is worth failing a render over — same treatment as `pwa-install-dismissed`.
 */

const DEVICE_ID_KEY = "push-device-id";
const PROMPT_DISMISSED_KEY = "push-prompt-dismissed";

/**
 * The `PushDevice.id` the server returned when this browser subscribed.
 *
 * This is how the UI recognises "this device" in the device list. Comparing endpoints would
 * work too, but the endpoint is a bearer URL and deliberately never leaves the server.
 */
export function readDeviceId(): number | null {
  try {
    const raw = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : null;
  } catch {
    // Not being able to remember which device we are is survivable — the toggle still works.
    return null;
  }
}

export function writeDeviceId(id: number): void {
  try {
    window.localStorage.setItem(DEVICE_ID_KEY, String(id));
  } catch {
    // Nothing to do.
  }
}

export function clearDeviceId(): void {
  try {
    window.localStorage.removeItem(DEVICE_ID_KEY);
  } catch {
    // Nothing to do.
  }
}

/** Whether the user closed the "Benachrichtigungen einschalten" banner. */
export function readPromptDismissed(): boolean {
  try {
    return window.localStorage.getItem(PROMPT_DISMISSED_KEY) === "true";
  } catch {
    // Failing to remember a dismissal is better than not rendering.
    return false;
  }
}

export function dismissPrompt(): void {
  try {
    window.localStorage.setItem(PROMPT_DISMISSED_KEY, "true");
  } catch {
    // The banner just comes back next session.
  }
}
